import { useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import styled from '@emotion/styled';
import { GenieIconNew } from '../SvgIcon/SvgIcon';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { setCurrentRecording } from '../../redux/actions/currentRecording';
import { openSnackbar } from '../../redux/actions/snackbarAction';

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--grey-50)',
    borderRadius: '5px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export default function SummarizeDialog({
  open,
  onClose,
  onSubmit,
  meetingTitle,
  meetingContext,
  loading,
  setMeetingAPIDetails,
  isMeetingAPIData,
}) {
  const dispatch = useDispatch();
  // Refs hold the latest field values for validation (avoids stale props due to React batching)
  const meetingTitleRef = useRef(meetingTitle ?? '');
  const meetingContextRef = useRef(meetingContext ?? '');
  const prevOpenRef = useRef(false);

  // Only sync refs from props when the dialog first opens. While open, refs are updated
  // by onChange; overwriting from props can replace user input with stale/empty values.
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      meetingTitleRef.current = meetingTitle ?? '';
      meetingContextRef.current = meetingContext ?? '';
    }
  }, [open, meetingTitle, meetingContext]);

  const handleSubmit = () => {
    const title = (typeof meetingTitleRef.current === 'string' ? meetingTitleRef.current : '').trim();
    const context = (typeof meetingContextRef.current === 'string' ? meetingContextRef.current : '').trim();
    if (title.length > 0 && context.length > 0) {
      // Pass current field values so submit uses what user entered (avoids race with Redux)
      onSubmit({ meetingTitle: title, meetingContext: context });
    } else {
      const field = [];
      if (title.length === 0) field.push('Meeting Title');
      if (context.length === 0) field.push('Meeting Context');
      dispatch(openSnackbar('error', field.join(', ') + ' is Required.'));
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle gap={1}>
        <GenieIconNew />
        <Typography
          variant="subtitle1"
          component={'span'}
          fontWeight={500}
          color="grey.800"
        >
          Summarize
        </Typography>
        <Tooltip title="Close">
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 13 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ '& > :not(:last-child)': { mb: 3 } }}
          noValidate
          autoComplete="off"
        >
          <Box>
            <Typography
              component="label"
              display="block"
              variant="subtitle1"
              color="grey.900"
            >
              Meeting Title <span style={{ color: '#ff5547' }}> *</span>
            </Typography>
            <CustomTextField
              fullWidth
              variant="outlined"
              size="small"
              defaultValue={meetingTitle}
              onChange={e => {
                const value = e.target.value;
                meetingTitleRef.current = value;
                if (isMeetingAPIData) {
                  setMeetingAPIDetails(prev => ({
                    ...prev,
                    meetingTitle: value,
                  }));
                } else {
                  dispatch(
                    setCurrentRecording({ meetingTitle: value })
                  );
                }
              }}
            />
          </Box>

          <Box>
            <Typography
              component="label"
              display="block"
              variant="subtitle1"
              color="grey.900"
            >
              Meeting Context <span style={{ color: '#ff5547' }}> *</span>
            </Typography>
            <CustomTextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={4}
              defaultValue={meetingContext}
              placeholder="Provide Context about the Meeting.."
              onChange={e => {
                const value = e.target.value;
                meetingContextRef.current = value;
                if (isMeetingAPIData && setMeetingAPIDetails) {
                  setMeetingAPIDetails(prev => ({
                    ...prev,
                    meetingContext: value,
                  }));
                } else {
                  dispatch(
                    setCurrentRecording({ meetingContext: value })
                  );
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <Divider sx={{ borderColor: 'var(--grey-100)' }} />
      <DialogActions
        sx={{
          '&.MuiDialogActions-root': {
            py: 1,
          },
        }}
      >
        <Button
          variant="text"
          startIcon={<CheckCircleIcon />}
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
